import api, { CustomerPendingSteps, CustomerWireInfo } from '@api';
import {
    CheckCircleOutline, Edit, ExpandLess, ExpandMore, RemoveCircleOutline
} from '@mui/icons-material';
import {
    IconButton, Paper, Tooltip, Typography
} from '@mui/material';
import { LabelGroup, LabeledValue } from '@tsp-ui/core/components';
import { useConfirm, usePageMessage } from '@tsp-ui/core/utils';
import {
    Dispatch, SetStateAction, useContext, useState
} from 'react';

import { CustomerDetailContext, CustomerDetailContextValue } from '../../../CustomerDetailPage';
import { WireInstructionsListContentProps } from '../WireInstructionsListContent';

import styles from './WireInstructionsCard.module.scss';


type WireInstructionsCardProps =
(WireInstructionsListContentProps & {
    wireInstructions: CustomerWireInfo;
    readOnly?: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
});

export default function WireInstructionsCard({
    wireInstructions,
    wireInstructionsArray,
    onApprove,
    onToggleActive,
    setWireInstructionsToEdit,
    readOnly,
    setLoading
}: WireInstructionsCardProps) {
    const [ expanded, setExpanded ] = useState(false);

    const pageMessage = usePageMessage();
    const confirm = useConfirm();

    const {
        updatePendingSteps
    } = useContext(CustomerDetailContext) as Required<CustomerDetailContextValue>;

    const toggleActive = async () => {
        try {
            if (!wireInstructions.isActive || await confirm('Are you sure you want to deactivate this wire instruction?')) {
                setLoading(true);

                await api.customer.wireInfo.updateWireInfo({
                    ...wireInstructions,
                    isActive: !wireInstructions.isActive
                });

                onToggleActive(wireInstructions.id);

                const length = wireInstructionsArray?.filter(x => x.isActive && x.isApproved).length;
                const deactivatingLastItem = length === 1 && wireInstructions.isActive;
                const activatingFirstItem = length === 0 && !wireInstructions.isActive;

                if (deactivatingLastItem || activatingFirstItem) {
                    await updatePendingSteps(CustomerPendingSteps.WIRE_INSTRUCTIONS);
                }

                pageMessage.success(`Wire instructions ${wireInstructions.isActive ? 'de' : ''}activated`);
            }
        } catch (error) {
            const action = wireInstructions.isActive ? 'deactivating' : 'activating';
            pageMessage.handleApiError(`An error occurred while ${action} the wire instructions`, error);
        }

        setLoading(false);
    };

    const approve = async () => {
        setLoading(true);

        try {
            await api.customer.wireInfo.updateWireInfo({
                ...wireInstructions,
                isApproved: true
            });

            onApprove(wireInstructions.id);

            if (wireInstructionsArray?.filter(x => x.isActive && x.isApproved).length === 0) {
                await updatePendingSteps(CustomerPendingSteps.WIRE_INSTRUCTIONS);
            }

            pageMessage.success('Wire instructions approved');
        } catch (error) {
            pageMessage.handleApiError('An error occurred while approving the wire instructions', error);
        }

        setLoading(false);
    };

    const {
        isActive,
        bankName,
        bankCity,
        bankState,
        isApproved,
        bankAccountNum,
        bankAba,
        wireDestination,
        furtherCreditToAcctName,
        furtherCreditToAcctNum
    } = wireInstructions;

    return (
        <Paper
            variant="outlined"
            className={styles.root}
        >
            <div className={styles.row}>
                <div>
                    <Typography>
                        {bankName}
                    </Typography>

                    <Typography
                        variant="body2"
                        component="p"
                    >
                        {`${bankCity}, ${bankState}`}
                    </Typography>
                </div>

                <div className={styles.actionsContainer}>
                    {isActive && !readOnly && (
                        <Tooltip title="Deactivate">
                            <IconButton
                                onClick={toggleActive}
                            >
                                <RemoveCircleOutline color="error" />
                            </IconButton>
                        </Tooltip>
                    )}

                    {!readOnly && (
                        <Tooltip title="Edit">
                            <IconButton
                                onClick={() => setWireInstructionsToEdit(wireInstructions)}
                            >
                                <Edit color="secondary" />
                            </IconButton>
                        </Tooltip>
                    )}

                    {!isApproved && isActive && !readOnly && (
                        <Tooltip title="Approve">
                            <IconButton
                                onClick={approve}
                            >
                                <CheckCircleOutline color="success" />
                            </IconButton>
                        </Tooltip>
                    )}

                    {!isActive && !readOnly && (
                        <Tooltip title="Activate">
                            <IconButton
                                onClick={toggleActive}
                            >
                                <CheckCircleOutline color="success" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            </div>

            <div className={styles.row}>
                <LabelGroup>
                    <LabeledValue
                        label="Account number:"
                        value={bankAccountNum}
                    />

                    <LabeledValue
                        label="ABA number:"
                        value={bankAba}
                    />

                    {expanded && (
                        <>
                            <LabeledValue
                                label="Wire destination:"
                                value={wireDestination}
                                classNames={{ label: styles.additionalInfoMargin }}
                            />

                            {(furtherCreditToAcctName || furtherCreditToAcctNum) && (
                                <LabeledValue
                                    label="Further credit to:"
                                    value={(
                                        <>
                                            {furtherCreditToAcctName}<br />

                                            {furtherCreditToAcctNum}
                                        </>
                                    )}
                                />
                            )}
                        </>
                    )}
                </LabelGroup>

                <Tooltip title={`Show ${expanded ? 'less' : 'more'}`}>
                    <IconButton
                        edge="end"
                        className={styles.expandButton}
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? (
                            <ExpandLess color="secondary" />
                        ) : (
                            <ExpandMore color="secondary" />
                        )}
                    </IconButton>
                </Tooltip>
            </div>
        </Paper>
    );
}
